
import React from 'react';
import { format, isAfter, isBefore, isToday } from 'date-fns';
import { Calendar, Clock } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { useTranslation } from 'react-i18next';
import { cn } from "@/lib/utils";

interface CardDatesProps {
  startDate?: string | null;
  dueDate?: string | null;
}

export const CardDates = ({ startDate, dueDate }: CardDatesProps) => {
  const { t } = useTranslation();

  const getDateStatus = (date: string) => {
    const today = new Date();
    const targetDate = new Date(date);
    
    if (isToday(targetDate)) return 'today';
    if (isBefore(targetDate, today)) return 'past';
    if (isAfter(targetDate, today)) return 'future';
    return 'normal';
  };

  const getDateBadgeStyles = (status: string) => {
    switch (status) {
      case 'today':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100';
      case 'past':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100';
      case 'future':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100';
    }
  };

  if (!startDate && !dueDate) return null;

  return (
    <div className="space-y-1.5">
      {startDate && (
        <Badge 
          variant="outline" 
          className={cn(
            "flex items-center gap-1.5 w-fit",
            getDateBadgeStyles(getDateStatus(startDate))
          )}
        >
          <Clock className="h-3 w-3" />
          <span className="text-xs font-medium">
            {t('common.startDate')}: {format(new Date(startDate), 'PPP')}
          </span>
        </Badge>
      )}
      {dueDate && (
        <Badge 
          variant="outline" 
          className={cn(
            "flex items-center gap-1.5 w-fit",
            getDateBadgeStyles(getDateStatus(dueDate))
          )}
        >
          <Calendar className="h-3 w-3" />
          <span className="text-xs font-medium">
            {t('common.dueDate')}: {format(new Date(dueDate), 'PPP')}
          </span>
        </Badge>
      )}
    </div>
  );
};
